import LoopbackClient from '../collaboration/LoopbackClient';
import {
  type CopiedWhiteboardContent,
  type DocData,
} from '../collaboration/Serialization';
import IntegrationsController from '../controllers/IntegrationsController';
import PresentationController from '../controllers/PresentationController';
import WhiteboardController from '../controllers/WhiteboardController';
import Canvas from '../objects/Canvas';
import ToolPaste from '../tools/ToolPaste';
import version from '../version';

async function createWhiteboardControllerForThumbnail(
  data: DocData
): Promise<WhiteboardController> {
  const controller = new WhiteboardController();
  const presentationController = new PresentationController();
  presentationController.internalInitialize(undefined, {
    mode: 'inline',
    setIsPresenting: () => null,
  });

  const integrationsController = new IntegrationsController();
  integrationsController.internalInitialize(undefined, { readOnly: false });

  controller.internalInitialize(undefined, {
    disableOwnThumbnailGeneration: true,
    readOnly: true,
    allowUpdate: false,
    initialData: data,
    resolveUserInfo: async id => ({
      displayName: `You Shouldn't See This ${id}`,
    }),
    resolveRecordInfo: async () => ({
      typename: 'Undefined',
    }),
    openAhaDrawer: () => {},
    openRecordInDrawer: () => {},
  });

  controller.sendShapeCreationAnalytics = false;

  const containerElement = document.createElement('div');
  const canvasElement = document.createElement('canvas');
  const externalElement = document.createElement('div');
  const portalElement = document.createElement('div');
  const options = {};

  controller.startController(presentationController, integrationsController);

  const canvas = new Canvas(
    controller,
    containerElement,
    canvasElement,
    externalElement,
    portalElement,
    options
  );

  controller.canvas = canvas;

  // we can't initialise with allowUpdate true, or the controller will (understandably) complain
  // about a missing connection. If we want a update-enabled whiteboard (needed in order to paste clipboard data in),
  // we need to first override the client with a no-op one, then we can simulate a prop change to allow updates
  controller.client = new LoopbackClient(controller);
  controller.internalInitialize(null, {
    ...controller.props,
    allowUpdate: true,
  });

  return controller;
}

async function download(filename: string, dataUrl: string) {
  const data = dataUrl.split(',').pop() || '';
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  const file = new File([bytes], filename, { type: 'image/png' });
  const url = URL.createObjectURL(file);

  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  a.click();

  // Clean up
  URL.revokeObjectURL(url);
}

/** Returns a PNG image from clipboard data by pasting it into a dummy
 * controller and then rendering a PNG from that controller. We require
 * a `controller` argument in order to check whether the controller that
 * is requesting thumbnails is supposed to be able to generate them. This
 * prevents us getting stuck in a loop of one controller creating a second
 * one to generate thumbnails, which in turn creates a third...
 */
export async function generatePNGFromClipboardData(
  controller: WhiteboardController,
  objects: Record<string, any>,
  // DEV/LOCAL ONLY: Triggers a download of the rendered image if provided. This is a convenience
  // if you have whiteboard JSON and you need to generate icons for the shapes menu.
  // eg for wireframe component blocks.
  downloadFilename?: string
): Promise<string | null> {
  if (controller.props.disableOwnThumbnailGeneration) {
    return null;
  }
  const controllerForThumbnail = await createWhiteboardControllerForThumbnail({
    objects: {},
  });
  const clipboardData: CopiedWhiteboardContent = {
    objects,
    version,
    whiteboardId: controllerForThumbnail.whiteboardId!,
  };
  // Any kind of error in generating a thumbnail shouldn't break the application
  try {
    // Move mouse out of the way so it doesn't draw 'hover' effects
    controllerForThumbnail.mousePosition = { x: -10000, y: -10000 };
    await ToolPaste.pasteContents(controllerForThumbnail, clipboardData, {
      x: 0,
      y: 0,
    });
    return await new Promise(resolve => {
      setTimeout(async () => {
        const dimensions = controllerForThumbnail.canvas.usedPortion(false);
        const result = controllerForThumbnail.canvas.renderToPng(dimensions);
        if (result.success) {
          if (downloadFilename) {
            await download(downloadFilename, result.dataUrl);
          }
          resolve(result.dataUrl);
        } else {
          console.error(
            'Thumbnail generation failed to render canvas to PNG image',
            result.error
          );
          resolve(null);
        }
      }, 1000);
    });
  } catch (err) {
    console.error('Error generating PNG thumbnail from clipboard data', err);
    return null;
  }
}
